/**
 * /forbidden — 403 fault route.
 *
 * Round 5 Item 7 — Lightning fault chrome for "you don't have access to this."
 * Cases:
 *   - Standard user lands on /admin/* (router gate redirects here)
 *   - User opens a record (chat session, prompt) not shared with them
 *   - Admin tries to mutate a platform-pricing field (D-194 admin-read-only)
 *
 * "Ask admin" CTA reuses the Round 3 admin-contact resolver (`fetchAdminContact`)
 * — mailto pre-filled with subject + body so the user gets one-click action.
 * Falls back to the home button when no admin contact resolves (rare edge case:
 * caller IS the only admin, or no admin has an email on file).
 */

import { useEffect, useState } from 'react';

import {
  BrandedFaultRoute,
  FaultCtaHome,
} from '../components/BrandedFaultRoute.tsx';
import { fetchAdminContact, type AdminContact } from '../lib/api.ts';

export function Forbidden() {
  const [adminContact, setAdminContact] = useState<AdminContact | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAdminContact()
      .then((res) => {
        if (!cancelled) setAdminContact(res.contact);
      })
      .catch(() => {
        // Silent fallback — Home CTA still renders.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const mailtoHref =
    adminContact !== null && adminContact.email !== null
      ? `mailto:${adminContact.email}?${new URLSearchParams({
          subject: "Access request for Olytic",
          body: `Hi ${adminContact.display_name.split(' ')[0] || 'there'},\r\n\r\nI hit a page in Olytic I don't have access to. Could you grant me access (or let me know if there's a reason I shouldn't have it)?\r\n\r\nThanks!`,
        })
          .toString()
          .replace(/\+/g, '%20')}`
      : null;

  return (
    <BrandedFaultRoute
      kind="locked"
      iconKind="permission-sets"
      eyebrow="Permission needed"
      title="You don't have access to this."
      body={
        adminContact !== null
          ? `Your workspace admin can grant access. Ask ${adminContact.display_name.split(' ')[0] || adminContact.display_name} when you have a moment.`
          : "Your workspace admin can grant access. Reach out to them when you have a moment."
      }
      actions={
        <>
          {mailtoHref !== null && adminContact !== null && (
            <a
              href={mailtoHref}
              className="btn btn-primary btn-sm"
              style={{ textDecoration: 'none' }}
              title={`Email ${adminContact.display_name}`}
            >
              Ask {adminContact.display_name.split(' ')[0] || 'admin'}
            </a>
          )}
          <FaultCtaHome label="Back to Home" />
        </>
      }
    />
  );
}
